import React from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { ParcelOrderList } from "Components/order-management/parcel-order-list";
import { Button } from "@life-line-lab/components-react";
import orderStatusesMap from "Data/order-statuses.json";
import useSession from "Hooks/session.hook";
import useAccessControlList from "Hooks/access-control.hook";
import { getStatusStyle } from "Helpers/get-status-colours";
import { toCapitalisedSpaced } from "Helpers/to-capitalised-spaced";
import { useOrderListingStates } from "Helpers/index";

const EnrouteTab = ({
  baseColumns2,
  handleRefreshData,
  handleDownloadAirwayBill,
}) => {
  const { t } = useTranslation();
  const { roles, roleTypes } = useSession();
  const permission = useAccessControlList({
    roleTypes: roleTypes,
    resource: "Order_Management",
  });

  const { selectedRows } = useOrderListingStates();

  const showUpdateOption = permission.rights.includes("UPDATE");

  const allColumns = baseColumns2.concat([
    {
      title: t("label.sender").toLocaleUpperCase(),
      dataIndex: ["senderID", "name"],
      ellipsis: true,
    },
    {
      title: t("label.dispatchedAt").toLocaleUpperCase(),
      dataIndex: ["updatedAt"],
      render: (updatedAt: any) => {
        return (
          <span>
            {moment(updatedAt).format("hh:mm a") || <span>&#11835;</span>}
          </span>
        );
      },
    },
    {
      title: t("txt.receiver").toLocaleUpperCase(),
      dataIndex: ["receiverInfo", "fullName"],
      ellipsis: true,
    },
    {
      title: t("label.status").toLocaleUpperCase(),
      dataIndex: ["orderStatusInfo", "orderStatus"],
      ellipsis: true,
      render: (orderStatus: any) => {
        const status = orderStatusesMap?.[orderStatus]?.[roles[0]["type"]];

        return (
          <span className="status-indicator" style={getStatusStyle(status)}>
            {status ? toCapitalisedSpaced(status) : ""}
          </span>
        );
      },
    },
    {
      title: t("label.airwaybills").toLocaleUpperCase(),
      dataIndex: ["airwayBill"],
      render: (airwayBill: any, record: any) => {
        if (airwayBill) {
          return (
            <div className="download-button">
              <Button
                label={
                  !airwayBill?.downloaded
                    ? t("action.download")
                    : "Download Again"
                }
                type="default"
                size="middle"
                onClick={() => {
                  const allOrderIDs = record.childrenOrderIDs
                    ? [record._id, ...record.childrenOrderIDs]
                    : [record._id];
                  handleDownloadAirwayBill(allOrderIDs);
                }}
              />
            </div>
          );
        } else {
          return <span>No airway bills found, contact customer service</span>;
        }
      },
    },
  ]);

  const handleDisableRowSelection = (record: any) => {
    return !record.airwayBill;
  };

  return (
    <ParcelOrderList
      columns={allColumns}
      additionalComponent={
        showUpdateOption && (
          <div className="btn-actions-container">
            <Button
              label={t("action.downloadAirwaybills")}
              type="primary"
              size="large"
              isDisabled={selectedRows.length > 0 ? false : true}
              onClick={() => {
                handleDownloadAirwayBill(
                  selectedRows?.flatMap((order) => [
                    order?._id,
                    ...(order?.childrenOrderIDs || []),
                  ]),
                );
              }}
            />
          </div>
        )
      }
      isCheckBoxShown={true}
      disableRowSelection={handleDisableRowSelection}
      filterItems={[]}
      radioButtonComponent={undefined}
      handleRefreshData={handleRefreshData}
    />
  );
};

export default EnrouteTab;
